import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import { ApiMethods } from '@models/enums';
import {
  AccountingBrowse,
  AccountingBrowseUpdateData,
  AccountingDetails,
  AccountingExportParams,
  AccountingFinancialTab,
  AccountingSummaryTab,
  PaginatedResponse,
} from '@models/interfaces';
import {
  AccountingFilterOptionsResponse,
  MainAccountingBrowseFilter,
} from '@models/types';
import {
  AccountingActions,
  BreadcrumbsActions,
  SOFToolActions,
} from '@store/actions';
import {
  AccountingEffects,
  ProjectCategoriesEffects,
  ProjectsEffects,
} from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class AccountingHttpService extends CommonHttpService {
  @StoredRequest(AccountingActions.getAccountingBrowseDataAction)
  getAccountingBrowse(
    params: MainAccountingBrowseFilter,
  ): Promise<PaginatedResponse<AccountingBrowse>> {
    return this.http.callApi.get(`${this.apiUrl}/accounting`, null, {
      params,
    });
  }

  @StoredRequest(SOFToolActions.getSOFToolBrowseFilters)
  getAccountingBrowseFiltersOptions(): Promise<AccountingFilterOptionsResponse> {
    return this.http.callApi.get(`${this.apiUrl}/accounting/filter-options`);
  }

  @StoredRequest(
    AccountingActions.getAccountingDetailsAction,
    BreadcrumbsActions.accounting,
  )
  async getAccounting(projectBundleID: string): Promise<AccountingDetails> {
    const resp = await this.http.callApi.get(
      `${this.apiUrl}/accounting/${projectBundleID}`,
    );

    ProjectsEffects.setPaceTasks(resp);

    ProjectsEffects.setHoldOptions(
      ProjectsEffects.setCurrentProvidenceAgent(
        ProjectCategoriesEffects.setProjectCategoriesFromDetails(
          resp.projectData,
        ),
      ),
    );

    return resp;
  }

  @StoredRequest(
    AccountingActions.getAccountingSummaryTab,
    BreadcrumbsActions.accounting,
  )
  async getAccountingSummary(
    projectBundleID: string,
  ): Promise<AccountingSummaryTab> {
    const response: AccountingSummaryTab = await this.http.callApi.get(
      `${this.apiUrl}/accounting/summary/${projectBundleID}`,
    );

    AccountingEffects.setPaceTasks(response);
    AccountingEffects.setCurrentProvidenceAgent(response.projectData);
    ProjectCategoriesEffects.setProjectCategories(
      response.projectData.categories,
    );

    return response;
  }

  @StoredRequest(AccountingActions.getAccountingFinancialTab)
  async getAccountingFinancial(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<AccountingFinancialTab> {
    const response: AccountingFinancialTab = await this.http.callApi.get(
      `${this.apiUrl}/accounting/financial/${projectBundleID}`,
    );

    return response;
  }

  @StoredRequest(AccountingActions.updateAccountingBrowseAction)
  async updateAccountingBrowse(
    id: string,
    body: AccountingBrowseUpdateData,
  ): Promise<AccountingBrowseUpdateData> {
    await this.http.callApi.patch(`${this.apiUrl}/accounting`, body, {
      params: { projectId: id },
    });

    return { ...body, id };
  }

  exportAccountingProjects({
    exportType,
    ...params
  }: AccountingExportParams): Promise<void> {
    return this.downloadFile(
      ApiMethods.Get,
      `${this.apiUrl}/accounting/report/${exportType.toLowerCase()}`,
      { params },
    );
  }
}
